import { Button, Flex, Skeleton, Text } from '@chakra-ui/react';

import { useNavigate } from 'react-router-dom';

import { format, parseISO } from 'date-fns';

import { DEFAULT_DATE_FORMAT } from '@aslanpay/portal-common-lib';

import useFetchIndividual from 'app/hooks/useFetchIndividual';

import { getEmployeeProfilePath } from 'app/utils/routes';

interface PreviousEmploymentSectionProps {
  previousIndividualId?: string;
}

/**
 * Shows the closed employment this record replaces, for an employee who was rehired.
 *
 * Renders nothing at all when the record carries no link, which is every employee who is not a rehire.
 */
function PreviousEmploymentSection({
  previousIndividualId = undefined,
}: PreviousEmploymentSectionProps) {
  const navigate = useNavigate();

  const {
    data: previousEmployment,
    isLoading,
    isError,
  } = useFetchIndividual(previousIndividualId, !!previousIndividualId);

  if (!previousIndividualId) {
    return null;
  }

  return (
    <Flex gap="5px" flexDir="column" w="full">
      <Text color="brand.black" textStyle="extraSmallRegular">
        Previous employment
      </Text>

      <Flex
        w="full"
        flexDir={{ base: 'column', md: 'row' }}
        justifyContent="start"
        alignItems={{ base: 'start', md: 'center' }}
        rowGap="20px"
        columnGap="10px"
      >
        {isLoading && <Skeleton height="20px" width="220px" my="15px" />}

        {isError && (
          <Text color="brand.black" textStyle="smallRegular" py="15px">
            Could not load the previous employment
          </Text>
        )}

        {!isLoading && !isError && !previousEmployment && (
          <Text color="brand.black" textStyle="smallRegular" py="15px">
            —
          </Text>
        )}

        {!isLoading && !isError && previousEmployment && (
          <>
            {/* The name is the link — it is the thing the reader wants to follow, so it carries the affordance
                rather than a separate call to action sitting beside it. */}
            <Button
              variant="link"
              size="sm"
              py="15px"
              // Chakra keeps its horizontal padding on a link button, which would set the name in from the
              // label and the other values in this column.
              px="0"
              justifyContent="flex-start"
              onClick={() => navigate(getEmployeeProfilePath(previousEmployment.id))}
            >
              {`${previousEmployment.firstName} ${previousEmployment.lastName}`}
            </Button>

            {previousEmployment.terminationDate && (
              <Text
                color="brand.black"
                textStyle="smallRegular"
                py="15px"
                ml={{ base: 0, md: 'auto' }}
              >
                {`Left on ${format(
                  parseISO(previousEmployment.terminationDate),
                  DEFAULT_DATE_FORMAT
                )}`}
              </Text>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
}

export default PreviousEmploymentSection;
